import Lottie from "lottie-web";
import loadingAnimation from "../../asset/lotties/loading_animation_attention.json";
import { useEffect, useState, useMemo } from "react";
import {
  useSelectTheme,
  useCreateDispatchActions,
  ProjectBbiDataDigest,
  useSelectBatchData,
  ParticipantGarminDataDigestMap,
  ProjectGarminConnectWearTime,
  ParticipantGarminConnectWearTimeMap,
} from "../../store";
import { RadioGroup as ReactRadioGroup, Radio as ReactRadio } from "react-radio-group";
import { RequestResult, t, useCurrentProjectState } from "../../lib";
import { PopupTemplate, PopupSize } from "./popup_template";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { CalendarGreyIcon } from "../../asset/image";

export interface ExportHeatmapDataPopupProps {
  garminConnectEnabled?: boolean;
  garminDirectBbiEnabled?: boolean;
  onClickCancel: () => void;
}

export const ExportHeatmapDataPopup = (props: ExportHeatmapDataPopupProps) => {
  const { color, fontWeight, fontSize } = useSelectTheme();
  const { garminConnectEnabled, garminDirectBbiEnabled, onClickCancel } = props;
  const [garminBbiDataDigestResult, setGarminBbiDataDigestResult] = useState<RequestResult | null>(
    null,
  );
  const [garminConnectWearTimeResult, setGarminConnectWearTimeResult] =
    useState<RequestResult | null>(null);
  const { doREQUEST_GARMIN_BBI_DATA_DIGEST_FETCH, doREQUEST_GARMIN_CONNECT_WEAR_TIME_DATA_FETCH } =
    useCreateDispatchActions();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<Date[]>([]);
  const yymmddRange = useMemo(() => {
    if (period.length > 1) {
      const startYymmdd = t.dateToYYMMDD(period[0]);
      const endYymmdd = t.dateToYYMMDD(period[1]);
      return [startYymmdd, endYymmdd];
    }
    return [];
  }, [period]);
  const yymmddList = useMemo(() => {
    if (yymmddRange.length > 1) {
      return t.toYYMMDDListFromRange(yymmddRange[0], yymmddRange[1]).map((ymd) => ymd.toString());
    } else {
      return [];
    }
  }, [yymmddRange]);

  // project data
  const { projectId, project } = useCurrentProjectState();
  const batchId = project?.batchList?.[0]?.id;
  const batchData = useSelectBatchData();
  const participantList = useMemo(() => {
    if (batchId) return batchData?.[batchId]?.participantList ?? [];
    else return [];
  }, [batchData, batchId]);

  useEffect(() => {
    if (garminBbiDataDigestResult?.success) {
      const data = garminBbiDataDigestResult.result.payload as {
        projectBbiDataDigest: ProjectBbiDataDigest;
      };
      const participantBbiDataDigestMap = data.projectBbiDataDigest.participantBbiDataDigestMap;
      if (participantBbiDataDigestMap) {
        const csvContent = convertToCsv(participantBbiDataDigestMap, 3600000);
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        createDownloadUrl(blob, `garmin_direct_wear_time(${yymmddRange[0]}-${yymmddRange[1]}).csv`);
      } else {
        window.alert("No data in selected range");
      }
    }
  }, [garminBbiDataDigestResult]);

  useEffect(() => {
    if (garminConnectWearTimeResult?.success) {
      const data = garminConnectWearTimeResult.result.payload as {
        projectGarminConnectWearTime: ProjectGarminConnectWearTime;
      };
      const participantDataDigestMap =
        data.projectGarminConnectWearTime.participantGarminConnectWearTimeMap;
      if (participantDataDigestMap) {
        const csvContent = convertToCsv(participantDataDigestMap, 60);
        const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
        createDownloadUrl(
          blob,
          `garmin_connect_wear_time(${yymmddRange[0]}-${yymmddRange[1]}).csv`,
        );
      } else {
        window.alert("No data in selected range");
      }
    }
  }, [garminConnectWearTimeResult]);

  const toCsv = (dataTable: string[][]): string | undefined => {
    if (!dataTable || !dataTable.length) return;

    const separator = ",";
    const csvContent = dataTable
      .map((row) => {
        return row.join(separator);
      })
      .join("\n");
    return csvContent;
  };

  const initalCsvDataTable = (): string[][] => {
    const dataTable: string[][] = [];
    const heatmapDataType =
      exportDataType === "connect" ? "heart rate" : exportDataType === "direct" ? "BBI" : "";

    const header = `Garmin ${exportDataType} ${heatmapDataType} heatmap summary of projectid: ${projectId}\n`;
    dataTable.push([header]);

    const dateList = yymmddList.map((yymmdd) => {
      const yy = yymmdd.slice(0, 2);
      const mm = yymmdd.slice(2, 4);
      const dd = yymmdd.slice(4);
      return `20${yy}-${mm}-${dd}`;
    });
    const keys = ["participantId", "Insignia"].concat(dateList);
    dataTable.push(keys);
    return dataTable;
  };

  const convertToCsv = (
    particiapntDataDigestMap: ParticipantGarminDataDigestMap | ParticipantGarminConnectWearTimeMap,
    hourDivider: number,
  ): string | undefined => {
    const dataTable = initalCsvDataTable();

    for (const p of participantList) {
      let insigina = p.insignia ?? "";
      insigina.replace(/"/g, '""');
      if (insigina.search(/("|,|\n)/g) >= 0) {
        insigina = `"${insigina}"`;
      }

      const row = [p.id, insigina];
      for (const yymmdd of yymmddList) {
        const participantDataDigest = particiapntDataDigestMap[p.id];
        if (participantDataDigest) {
          const digest = participantDataDigest[yymmdd];
          let wearTime = 0;

          if (typeof digest === "number") {
            wearTime = digest / hourDivider;
          } else if (digest && typeof digest.wearTime === "number") {
            wearTime = digest.wearTime / hourDivider;
          }
          const wearTimePercent = (wearTime / 24) * 100;
          row.push(`${wearTime.toFixed(2)} (${Math.round(wearTimePercent)}%)`);
        } else {
          row.push("0 (0%)");
        }
      }
      dataTable.push(row);
    }
    return toCsv(dataTable);
  };

  const createDownloadUrl = (blob: Blob, fileName: string) => {
    try {
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download error:", error);
      window.alert(`Download file error: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const description = () => {
    return (
      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          <span
            css={{
              wordWrap: "break-word",
              fontWeight: fontWeight.medium,
              fontSize: fontSize.h6,
              color: color.textIcon.secondary,
            }}
          >
            Description here......
          </span>
        </div>
      </div>
    );
  };

  const handleChangePeriod = (value: DateObject[] | null) => {
    if (!value) return;
    setPeriod(value.map((dateObject) => dateObject.toDate()));
  };

  const [exportDataType, setExportDataType] = useState<"direct" | "connect">(
    garminConnectEnabled ? "connect" : "direct",
  );
  const handleChangeType = (e: "direct" | "connect") => {
    setExportDataType(e);
  };

  const onSubmit = () => {
    if (exportDataType == "connect") {
      doREQUEST_GARMIN_CONNECT_WEAR_TIME_DATA_FETCH({
        setRequestResult: setGarminConnectWearTimeResult,
        payload: {
          projectId,
          yymmddIndexList: t.toYYMMDDListFromRange(yymmddRange[0], yymmddRange[1]),
        },
      });
    }
    if (exportDataType == "direct") {
      doREQUEST_GARMIN_BBI_DATA_DIGEST_FETCH({
        setRequestResult: setGarminBbiDataDigestResult,
        payload: {
          projectId,
          yymmddIndexList: t.toYYMMDDListFromRange(yymmddRange[0], yymmddRange[1]),
        },
      });
    }
  };

  return (
    <div>
      <PopupTemplate
        {...{
          title: "Export Garmin Data Heatmap CSV File",
          background: true,
          popupSize: PopupSize.Tall,
          handleClosePopup: onClickCancel,
          mainFunction: onSubmit,
          buttonContent: "Download",
          buttonDisabled: yymmddList.length == 0,
        }}
      >
        <div> Select Date Range </div>
        <div
          css={{
            position: "relative",
            margin: "16px",
            height: "60px",
          }}
        >
          <img
            css={{
              position: "absolute",
              top: "11px",
              left: "50px",
            }}
            src={CalendarGreyIcon}
            width={20}
          />
          <div
            css={{
              position: "absolute",
              left: "80px",
            }}
          >
            <DatePicker
              value={period}
              onChange={handleChangePeriod}
              range={true}
              rangeHover={true}
              format="MMM D, YYYY"
              maxDate={new Date()}
              disabled={false}
              style={{
                padding: "9px 16px",
                width: "260px",
                height: "42px",
                fontWeight: fontWeight.medium,
                border: `1px solid ${color.grey_160}`,
                color: color.black,
              }}
            />
          </div>
        </div>

        <div css={{ marginTop: "16px", justifyContent: "start" }}>
          <p>Select the data type to export</p>
          <ReactRadioGroup
            name="dataTypeSelector"
            onChange={handleChangeType}
            css={{ marginTop: "16px", marginBottom: "16px", marginLeft: "70px" }}
          >
            {garminConnectEnabled && (
              <label
                key={"connect"}
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  cursor: "pointer",
                  paddingBottom: "12px",
                }}
              >
                <ReactRadio
                  value={"connect"}
                  css={{ margin: "0", marginRight: "16px" }}
                  checked={exportDataType === "connect"}
                />
                Garmin Connect Heart Rate Heatmap
              </label>
            )}

            {garminDirectBbiEnabled && (
              <label
                key={"direct"}
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  cursor: "pointer",
                  paddingBottom: "12px",
                }}
              >
                <ReactRadio
                  value={"direct"}
                  css={{ margin: "0", marginRight: "16px" }}
                  checked={exportDataType === "direct"}
                />
                Garmin Direct BBI Data Heatmap
              </label>
            )}
          </ReactRadioGroup>
        </div>
      </PopupTemplate>
    </div>
  );
};
